import { useEffect, useState } from "react";
import { Typography, Button, LinearProgress, TextField, Grid } from "@mui/material";
import { tss } from "tss-react";
import { CloudUpload } from "@mui/icons-material";
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { ModalWindow } from "components";
import uploadFileToBlob, { isStorageConfigured, setStorageCfg } from "components/azure-storage-blob";
import ApiUtils from "api/Utils";
import AutheService from "services/AuthService";

const useStyles = tss.create({
    root: {
        position: "relative",
        top: 10,
    },
    title: {
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 20,
    },
    upload: {
        position: "relative",
        top: 6,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    button: {
        minWidth: 20,
        padding: "5px 5px",
        marginLeft: 5,
        width: 75,
        height: 36,
    },
    bottom: {
        position: "absolute",
        top: "calc(100% - 49px)",
        right: 22,
    },
    nextButton: {
        fontWeight: "bold",
    },
    cancelButton: {
        right: 14,
        fontWeight: "bold",
    },
    grid: {
        width: "60%",
    },
    dropZone: {
        width: "100%",
        border: "dashed",
        cursor: "pointer",
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        minHeight: 250,
        borderColor: "rgba(0, 0, 0, 0.12)",
        borderRadius: 4,
        backgroundColor: "#f0f0f0",
    },
    dropZoneText: {
        marginTop: 24,
        marginBottom: 24,
        fontSize: "1.5rem",
        fontWeight: 400,
        lineHeight: 1.334,
        letterSpacing: "0em",
    },
});

type IManifest = {
    user: string;
    comment: string;
    capture_date: string;
    product: string;
    files: string[];
    project: string;
    entity: string;
};

type IProps = {
    open: boolean;
    handleClose: () => void;
    product: string;
    project: string;
};

export default function UploadVersionPanel(props: IProps) {
    const { open, product, project } = props;
    const { classes } = useStyles();
    const [comment, setComment] = useState("");
    const [files, setFiles] = useState<any>([]);
    const [uploading, setUploading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [failed, setFailed] = useState(false);
    const [errorInfo, setErrorInfo] = useState("");
    const [selectedDate, setDate] = useState(new Date());
    const [storageConfigured, setStorageConfigured] = useState(isStorageConfigured());

    const [uploadFile, setUploadFile] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [filesProcessed, setFilesProcessed] = useState(0);
    const [abort, setAbort] = useState<AbortController | null>(null);
    const [workflowFolder, setWorkflowFolder] = useState("");

    const getProductFolder = (project: string, product: string) => {
        ApiUtils.getEntity(project, project, (entity: any) => {
            let field_id = `workflow_folder_${product}`;

            if (entity.hasOwnProperty(field_id)) {
                setWorkflowFolder(entity[field_id]);
            }
        });
    };

    useEffect(() => {
        ApiUtils.getPlatformVersion((result: any) => {
            let sas_token: string = atob(result.upload_sas_token);
            setStorageCfg(result.upload_container, sas_token, result.upload_storage);
            setStorageConfigured(isStorageConfigured());
        });
        getProductFolder(project, product);
    }, [project, product]);

    const handleClose = () => {
        setUploading(false);
        setFailed(false);
        setErrorInfo("");
        props.handleClose();
        if (uploading) {
            if (abort !== null) {
                abort.abort();
            }
            setUploading(false);
        }
        setComplete(false);
    };

    const uploadVersionFiles = async (evt: any) => {
        if (complete || failed) {
            handleClose();
        } else {
            if (files && files.length === 0) {
                return false;
            }
            var _abort: AbortController = new AbortController();
            setAbort(_abort);
            setUploading(true);

            let files_upload: string[] = [];
            let folder: string = "app_" + Math.round(Math.random() * 0xffffffff).toString(16) + "/";
            let index: number = 0;
            try {
                for (const file of files) {
                    setUploadFile(file.name);
                    setUploadProgress(0);
                    setFilesProcessed(index);
                    await uploadFileToBlob(
                        file,
                        folder,
                        (value) => {
                            if (value !== 0) {
                                setUploadProgress((value / file.size) * 100);
                            }
                        },
                        _abort.signal
                    );
                    index++;
                    files_upload.push(file.name);
                }

                let tempEntity = {
                    file_folder: workflowFolder,
                    name: "New Version",
                    state: "COMPLETE",
                    file_type: "RAW",
                    file_date: new Date().getTime() + "",
                    user: AutheService.getUserId(),
                    type: "FILE",
                };

                ApiUtils.createEntity(project, tempEntity, async (respond: any) => {
                    if (respond.hasOwnProperty("id")) {
                        var manifest: IManifest = {
                            user: AutheService.getUserId(),
                            project: project,
                            product: product,
                            files: files_upload,
                            capture_date: new Date(selectedDate).getTime().toString(),
                            comment: comment,
                            entity: respond["id"],
                        };

                        console.log("upload manifest:", manifest);

                        var jsmanifest = JSON.stringify(manifest);
                        var blob = new Blob([jsmanifest], { type: "application/json" });
                        var file = new File([blob], "manifest.json");
                        await uploadFileToBlob(
                            file,
                            folder,
                            (value) => {
                                if (value !== 0) {
                                    setUploadProgress((value / file.size) * 100);
                                }
                            },
                            _abort.signal
                        );
                        setComplete(true);
                    } else {
                        setFailed(true);
                        let error_info: string = atob(respond["error_info"]);
                        setErrorInfo(error_info);
                        console.log(`Error loading, ${error_info}`);
                    }
                });
            } catch (error: any) {
                console.log(error as Error);
                if (_abort.signal.aborted) {
                    setAbort(null);
                } else {
                    // do something, and show error
                    setFailed(true);
                    setErrorInfo(error.message);
                    console.log(`Error loading, ${error}`);
                }
            }

            setUploading(false);
        }
    };

    const handleChangeComment = () => (event: any) => {
        const value = event.target.value;
        setComment(value);
    };

    const handleDateChange = (date: any) => {
        setDate(date);
    };

    const onChange = (event: any) => {
        var curFiles: any = [...files];
        curFiles = curFiles.concat(Object.values(event.target.files));
        setFiles(curFiles);
        //console.log("curFiles:", curFiles);
    };

    const onDrop = (event: any) => {
        event.preventDefault();
        if (event.dataTransfer.files) {
            var curFiles: any = [...files];
            var dropFiles = [];
            dropFiles = Object.values(event.dataTransfer.files);
            curFiles = curFiles.concat(dropFiles);
            setFiles(curFiles);
            //console.log("curFiles:", curFiles);
        }
    };

    const onDragOver = (event: any) => {
        // Prevent default behavior (Prevent file from being opened)
        event.preventDefault();
    };

    console.log(`workflowFolder, ${workflowFolder}`);
    return (
        <ModalWindow width={600} height={500} minHeight={500} open={open} handleClose={handleClose}>
            <Typography className={classes.title}>Create New Version</Typography>
            <Grid container style={{ width: "100%" }}>
                <Grid item xs={8}>
                    <TextField
                        id="new-comment"
                        label="Comment"
                        style={{ marginBottom: 30, width: "100%" }}
                        onChange={handleChangeComment()}
                        inputProps={{ "aria-label": "Comment" }}
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={4}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container>
                            <MobileDateTimePicker
                                label="Capture Date"
                                format="yyyy-MM-dd"
                                value={selectedDate}
                                onChange={handleDateChange}
                                sx={{
                                    "& fieldset": {
                                        borderRadius: 0,

                                        border: "none",
                                        borderBottom: "1px solid #000000de",
                                    },
                                    "& input": { py: "12.5px" },
                                }}
                                slotProps={{
                                    toolbar: {
                                        sx: {
                                            "& .MuiDateTimePickerToolbar-timeContainer": {
                                                marginTop: "14px",
                                            },
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </LocalizationProvider>
                </Grid>
            </Grid>
            {!complete && !failed && storageConfigured && !uploading && (
                <div className={classes.dropZone} onDrop={onDrop} onDragOver={onDragOver}>
                    <input
                        id="file-select"
                        type="file"
                        multiple={true}
                        accept=""
                        onChange={onChange}
                        style={{ display: "none" }}
                    />
                    <label htmlFor="file-select">
                        <div style={{ textAlign: "center" }}>
                            <Typography className={classes.dropZoneText} style={{ marginTop: 24, marginBottom: 24 }}>
                                Drag and drop a file here or click
                            </Typography>
                            <CloudUpload style={{ width: 51, height: 51 }} />
                        </div>
                    </label>
                </div>
            )}
            {storageConfigured && uploading && (
                <>
                    <Typography>
                        Files uploaded: {filesProcessed} / {files.length}
                    </Typography>
                    {uploadFile}
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" component="div">
                        {`${Math.round(uploadProgress)}%`}
                    </Typography>
                </>
            )}
            {uploading && (
                <Typography style={{ bottom: 70, position: "absolute" }}>
                    *Please keep this tab open until the upload process completes.
                </Typography>
            )}
            {!complete && !failed && !uploading && files.length === 0 && (
                <Typography style={{ bottom: 60, position: "absolute" }}>No files selected.</Typography>
            )}
            {!complete && !failed && !uploading && files.length > 0 && (
                <Typography style={{ bottom: 60, position: "absolute" }}>{files.length} file(s) selected.</Typography>
            )}
            {complete && !failed && (
                <div>
                    <Typography style={{ marginTop: 20 }}>File uploading completed successfully.</Typography>
                    <Typography style={{ marginTop: 20 }}>
                        A new version will appear once the files are received. This could take from a few seconds to
                        minutes. If the new version does not show after a while, please refresh the page.
                    </Typography>
                </div>
            )}
            {failed && (
                <div>
                    <Typography style={{ marginTop: 20 }}>File upload failed.</Typography>
                    <Typography style={{ marginTop: 20, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {errorInfo}
                    </Typography>
                </div>
            )}
            <div className={classes.bottom}>
                {!failed && !complete && (
                    <Button
                        onClick={handleClose}
                        className={[classes.button, classes.cancelButton].join(" ")}
                        color="primary"
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    onClick={uploadVersionFiles}
                    color="primary"
                    variant="contained"
                    disabled={!complete && (files.length === 0 || uploading)}
                    className={[classes.button, classes.nextButton].join(" ")}
                >
                    {complete || failed ? "Done" : "Upload"}
                </Button>
            </div>
        </ModalWindow>
    );
}
